import type { ReactNode } from 'react'
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react'
import { Snackbar, Alert, IconButton, Button, Box, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Close } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import type { TransitionProps } from '@mui/material/transitions'
import { randomIdGenerator, type IdGenerator } from '../lib/id'
import { browserScheduler, type Scheduler } from '../lib/scheduler'
import { getBrowserAdapter, type BrowserAdapter } from '../lib/browserAdapter'

const DEFAULT_DURATION = 3000

const NoopTransition = React.forwardRef(function NoopTransition(
    { children }: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>
) {
    if (ref) {
        return React.cloneElement(children, { ref } as React.Attributes)
    }
    return children
})

export interface Notification {
    id: string
    message: string
    severity: 'success' | 'error' | 'warning' | 'info'
    duration: number
    onRetry?: () => void
}

export interface NotificationOptions {
    values?: Record<string, string | number> // For interpolation: t(key, { value: 123 })
    severity?: 'success' | 'error' | 'warning' | 'info'
    duration?: number
    onRetry?: () => void
}

interface NotificationContextType {
    notifications: Notification[]
    addNotification: (notification: {
        id?: string
        message: string
        type: Notification['severity']
        duration?: number
    }) => void
    removeNotification: (id: string) => void
    clearNotifications: () => void
    showNotification: (translationKey: string, options?: NotificationOptions) => void
    showSuccess: (translationKey: string, options?: Omit<NotificationOptions, 'severity'>) => void
    showError: (translationKey: string, options?: Omit<NotificationOptions, 'severity'>) => void
    showWarning: (translationKey: string, options?: Omit<NotificationOptions, 'severity'>) => void
    showInfo: (translationKey: string, options?: Omit<NotificationOptions, 'severity'>) => void
}

// Justified exception: Context export (not a component) - React Refresh limitation
// eslint-disable-next-line react-refresh/only-export-components -- Justified: context export
export const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

interface NotificationProviderProps {
    children: ReactNode
    /** Optional ID generator for testability. Defaults to randomIdGenerator. */
    idGenerator?: IdGenerator
    /** Optional scheduler for testability. Defaults to browserScheduler. */
    scheduler?: Scheduler
    /** Optional browser adapter for testability. Defaults to browserAdapter. */
    browserAdapter?: BrowserAdapter
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
    children,
    idGenerator = randomIdGenerator,
    scheduler = browserScheduler,
    browserAdapter: adapter = getBrowserAdapter(),
}) => {
    const [notifications, setNotifications] = useState<Notification[]>([])
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
    const { t } = useTranslation()

    const showNotification = useCallback(
        (translationKey: string, options?: NotificationOptions) => {
            const severity = options?.severity || 'info'
            const duration = options?.duration ?? DEFAULT_DURATION
            // Translate the key internally using t() with interpolation values
            const message = t(translationKey, options?.values)

            const id = idGenerator.generate()
            const notification: Notification = {
                id,
                message,
                severity,
                duration,
                onRetry: options?.onRetry,
            }

            setNotifications((prev) => {
                // Dedupe by message and severity to prevent stacking
                const existing = prev.find((n) => n.message === message && n.severity === severity)
                if (existing) {
                    return prev
                }
                return [...prev, notification]
            })

            // Auto-remove after duration
            if (duration > 0) {
                scheduler.setTimeout(() => {
                    setNotifications((prev) => prev.filter((n) => n.id !== id))
                }, duration)
            }
        },
        [t, idGenerator, scheduler]
    )

    const showSuccess = useCallback(
        (translationKey: string, options?: Omit<NotificationOptions, 'severity'>) => {
            showNotification(translationKey, { ...options, severity: 'success' })
        },
        [showNotification]
    )

    const showError = useCallback(
        (translationKey: string, options?: Omit<NotificationOptions, 'severity'>) => {
            showNotification(translationKey, { ...options, severity: 'error' })
        },
        [showNotification]
    )

    const showWarning = useCallback(
        (translationKey: string, options?: Omit<NotificationOptions, 'severity'>) => {
            showNotification(translationKey, { ...options, severity: 'warning' })
        },
        [showNotification]
    )

    const showInfo = useCallback(
        (translationKey: string, options?: Omit<NotificationOptions, 'severity'>) => {
            showNotification(translationKey, { ...options, severity: 'info' })
        },
        [showNotification]
    )

    const handleClose = useCallback((id: string) => {
        setNotifications((prev) => prev.filter((n) => n.id !== id))
    }, [])

    const addNotification = useCallback(
        (notification: {
            id?: string
            message: string
            type: Notification['severity']
            duration?: number
        }) => {
            const id = notification.id || idGenerator.generate()
            const normalizedDuration = notification.duration ?? DEFAULT_DURATION
            const newNotification: Notification = {
                id,
                message: notification.message,
                severity: notification.type,
                duration: normalizedDuration,
            }

            setNotifications((prev) => {
                // Dedupe by message and severity to prevent stacking
                const existing = prev.find(
                    (n) => n.message === notification.message && n.severity === notification.type
                )
                if (existing) {
                    return prev
                }
                return [...prev, newNotification]
            })

            // Auto-remove after duration
            if (newNotification.duration > 0) {
                scheduler.setTimeout(() => {
                    setNotifications((prev) => prev.filter((n) => n.id !== id))
                }, newNotification.duration)
            }
        },
        [idGenerator, scheduler]
    )

    const removeNotification = useCallback((id: string) => {
        setNotifications((prev) => prev.filter((n) => n.id !== id))
    }, [])

    const clearNotifications = useCallback(() => {
        setNotifications([])
    }, [])

    const value = useMemo<NotificationContextType>(
        () => ({
            notifications,
            addNotification,
            removeNotification,
            clearNotifications,
            showNotification,
            showSuccess,
            showError,
            showWarning,
            showInfo,
        }),
        [
            notifications,
            addNotification,
            removeNotification,
            clearNotifications,
            showNotification,
            showSuccess,
            showError,
            showWarning,
            showInfo,
        ]
    )

    return (
        <NotificationContext.Provider value={value}>
            {children}
            {/* Render notifications */}
            {notifications.map((notification, index) => {
                const prefersReducedMotion = adapter.matchMedia(
                    '(prefers-reduced-motion: reduce)'
                ).matches

                // Use null to disable auto-hide when duration is 0 or negative
                const autoHideDuration =
                    typeof notification.duration === 'number'
                        ? notification.duration > 0
                            ? notification.duration
                            : null
                        : notification.duration // undefined → use MUI default

                return (
                    <Snackbar
                        key={notification.id}
                        open={true}
                        autoHideDuration={autoHideDuration}
                        onClose={() => handleClose(notification.id)}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: isMobile ? 'center' : 'right',
                        }}
                        sx={{
                            // Ensure no layout shift
                            position: 'fixed',
                            zIndex: theme.zIndex.snackbar,
                            // Prevent multiple snackbars from overlapping
                            bottom: isMobile ? 16 : 16 + index * 70,
                            right: isMobile ? 'auto' : 16,
                            left: isMobile ? 16 : 'auto',
                        }}
                        transitionDuration={prefersReducedMotion ? 0 : undefined}
                        // Respect prefers-reduced-motion
                        slots={{
                            transition: prefersReducedMotion ? NoopTransition : undefined,
                        }}
                    >
                        <Alert
                            severity={notification.severity}
                            onClose={() => handleClose(notification.id)}
                            action={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {notification.onRetry && (
                                        <Button
                                            size="small"
                                            color="inherit"
                                            onClick={() => {
                                                notification.onRetry?.()
                                                handleClose(notification.id)
                                            }}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            {t('common:retry')}
                                        </Button>
                                    )}
                                    <IconButton
                                        size="small"
                                        aria-label={t('common:close')}
                                        color="inherit"
                                        onClick={() => handleClose(notification.id)}
                                    >
                                        <Close fontSize="small" />
                                    </IconButton>
                                </Box>
                            }
                            sx={{
                                minWidth: isMobile ? '280px' : '320px',
                                maxWidth: isMobile ? 'calc(100vw - 32px)' : '400px',
                            }}
                            // Screen reader announcement
                            role="alert"
                            aria-live="polite"
                        >
                            {notification.message}
                        </Alert>
                    </Snackbar>
                )
            })}
        </NotificationContext.Provider>
    )
}

// Justified exception: Custom hook export (not a component) - React Refresh limitation
// eslint-disable-next-line react-refresh/only-export-components -- Justified: custom hook export
export const useNotifications = (): NotificationContextType => {
    const context = useContext(NotificationContext)
    if (context === undefined) {
        throw new Error('useNotifications must be used within a NotificationProvider')
    }
    return context
}

/**
 * Selector hook that returns only the notifications array.
 * Components using this hook will only re-render when notifications change.
 */
// Justified exception: Custom hook export (not a component) - React Refresh limitation
// eslint-disable-next-line react-refresh/only-export-components -- Justified: custom hook export
export const useNotificationList = (): Notification[] => {
    const context = useContext(NotificationContext)
    if (context === undefined) {
        throw new Error('useNotificationList must be used within a NotificationProvider')
    }
    // Memoize to prevent re-renders when actions change
    return useMemo(() => context.notifications, [context.notifications])
}

/**
 * Selector hook that returns only notification action functions.
 * Components using this hook will only re-render when action functions change (rare).
 */
// Justified exception: Custom hook export (not a component) - React Refresh limitation
// eslint-disable-next-line react-refresh/only-export-components -- Justified: custom hook export
export const useNotificationActions = () => {
    const context = useContext(NotificationContext)
    if (context === undefined) {
        throw new Error('useNotificationActions must be used within a NotificationProvider')
    }
    // Memoize to prevent re-renders when notifications array changes
    return useMemo(
        () => ({
            addNotification: context.addNotification,
            removeNotification: context.removeNotification,
            clearNotifications: context.clearNotifications,
            showNotification: context.showNotification,
            showSuccess: context.showSuccess,
            showError: context.showError,
            showWarning: context.showWarning,
            showInfo: context.showInfo,
        }),
        [
            context.addNotification,
            context.removeNotification,
            context.clearNotifications,
            context.showNotification,
            context.showSuccess,
            context.showError,
            context.showWarning,
            context.showInfo,
        ]
    )
}
